import apiClient from '../apiClient';

export interface AuditLogEntry {
  id: string;
  userId: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    role: {
      name: string;
    };
  };
  action: string;
  category: 'authentication' | 'patient_access' | 'data_modification' | 'system_config' | 'user_management' | 'compliance';
  resource: string;
  resourceId?: string;
  outcome: 'success' | 'failure' | 'warning';
  ipAddress: string;
  userAgent: string;
  sessionId: string;
  details: Record<string, unknown>;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  complianceFlags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AuditLogQueryParams {
  search?: string;
  category?: string;
  outcome?: string;
  riskLevel?: string;
  startDate?: string;
  endDate?: string;
  userId?: string;
  page?: number;
  limit?: number;
}

export interface AuditLogResponse {
  data: AuditLogEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AuditLogStatistics {
  total: number;
  failedActions: number;
  highRiskEvents: number;
  uniqueUsers: number;
  byCategory: Record<string, number>;
}

export interface ComplianceReport {
  id: string;
  name: string;
  type: 'hipaa' | 'gdpr' | 'custom';
  status: 'pending' | 'generating' | 'ready' | 'failed';
  periodStart: string;
  periodEnd: string;
  totalEntries: number;
  violations: number;
  generatedAt?: string;
  generatedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  summary?: Record<string, unknown>;
  filters?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateComplianceReportDto {
  name: string;
  type: 'hipaa' | 'gdpr' | 'custom';
  periodStart: string;
  periodEnd: string;
  filters?: Record<string, unknown>;
}

export const auditLogsApi = {
  /**
   * Get audit logs with filters and pagination
   */
  async getAuditLogs(params: AuditLogQueryParams): Promise<AuditLogResponse> {
    const response = await apiClient.get('/audit-logs', { params });
    return response.data;
  },

  /**
   * Get a single audit log by ID
   */
  async getAuditLog(id: string): Promise<AuditLogEntry> {
    const response = await apiClient.get(`/audit-logs/${id}`);
    return response.data;
  },

  /**
   * Get audit log statistics
   */
  async getStatistics(startDate?: string, endDate?: string): Promise<AuditLogStatistics> {
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const response = await apiClient.get('/audit-logs/statistics', { params });
    return response.data;
  },

  /**
   * Create a new audit log entry
   */
  async createAuditLog(data: Partial<AuditLogEntry>): Promise<AuditLogEntry> {
    const response = await apiClient.post('/audit-logs', data);
    return response.data;
  },

  // ==================== Compliance Reports ====================

  /**
   * Get all compliance reports
   */
  async getComplianceReports(): Promise<ComplianceReport[]> {
    const response = await apiClient.get('/audit-logs/compliance-reports');
    return response.data.data
  },

  /**
   * Get a single compliance report by ID
   */
  async getComplianceReport(id: string): Promise<ComplianceReport> {
    const response = await apiClient.get(`/audit-logs/compliance-reports/${id}`);
    return response.data.data;
  },

  /**
   * Create a new compliance report
   */
  async createComplianceReport(data: CreateComplianceReportDto): Promise<ComplianceReport> {
    const response = await apiClient.post('/audit-logs/compliance-reports', data);
    return response.data.data;
  },

  /**
   * Delete a compliance report
   */
  async deleteComplianceReport(id: string): Promise<void> {
    await apiClient.delete(`/audit-logs/compliance-reports/${id}`);
  },

  /**
   * Download a compliance report as PDF
   */
  async downloadComplianceReportPDF(id: string): Promise<Blob> {
    const response = await apiClient.get(`/audit-logs/compliance-reports/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },
};
